import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { Rating } from 'commons/src/models/commonEnums';
import { sensorRating, getStatusColor, sensorShowsRatingLabels } from 'commons/src/sensorRatingUtils';
import { PDFContext, ReportSensorThresholds } from '../../../../../models/common';
import { formatSensorUnit, SpaceMeasurementData, SpaceMeasurementDataColumns } from '../utils/pdfHelpers';
import iaqInsight2PdfStyles from '../utils/pdfStyles';

type IaqInsight2DataTableProps = {
    translationFunction: PDFContext;
    sensorType: string;
    sensorThresholdConfiguration: ReportSensorThresholds;
    spaceMeasurementColumns: SpaceMeasurementDataColumns;
};

const IaqInsight2DataTable = ({
    translationFunction,
    sensorType,
    sensorThresholdConfiguration,
    spaceMeasurementColumns,
}: IaqInsight2DataTableProps): React.ReactElement | null => {
    const { ranges: thresholdRanges, unit: measurementUnit } = sensorThresholdConfiguration;
    const formattedMeasurementUnit = formatSensorUnit(sensorType, measurementUnit);

    const getValueStatusWithRating = (
        measurementValue: number | undefined
    ): { value: string; unit: string; rating: Rating | undefined; color: string } | null => {
        if (measurementValue === undefined) {
            return null;
        }
        const measurementValueRating = sensorRating(thresholdRanges, measurementValue);
        const statusColorForValue = measurementValueRating ? getStatusColor(measurementValueRating) : '#21282D';
        return {
            value: String(measurementValue),
            unit: formattedMeasurementUnit,
            rating: measurementValueRating,
            color: statusColorForValue,
        };
    };

    // Don't render table if there are no spaces at all
    if (!spaceMeasurementColumns || spaceMeasurementColumns.length === 0) {
        return null;
    }

    return (
        <View style={iaqInsight2PdfStyles.tableContainer}>
            {/* Table Rows - header only shown once at the start */}
            {spaceMeasurementColumns.map((spaceMeasurementData: SpaceMeasurementData, index: number) => {
                const averageValueStatus = getValueStatusWithRating(spaceMeasurementData.average);
                const lowestMeasurementStatus = getValueStatusWithRating(spaceMeasurementData.lowestMeasurement);
                const highestMeasurementStatus = getValueStatusWithRating(spaceMeasurementData.highestMeasurement);

                const showHeader = index === 0;

                return (
                    <View key={`${spaceMeasurementData.serialNumber}-${sensorType}`} wrap={false}>
                        {/* Table Header - shown before first row and every 12 rows for pagination */}
                        {showHeader && (
                            <View style={iaqInsight2PdfStyles.tableHeaderRow}>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '10%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.Floor')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '30%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('Space.SpaceName')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.AverageValue')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.LowestMeasurement')}
                                    </Text>
                                </View>
                                <View style={[iaqInsight2PdfStyles.tableHeaderCell, { width: '20%' }]}>
                                    <Text style={iaqInsight2PdfStyles.tableHeaderText}>
                                        {translationFunction('IAQReport2.HighestMeasurement')}
                                    </Text>
                                </View>
                            </View>
                        )}

                        <View style={iaqInsight2PdfStyles.tableRow}>
                            {/* Floor */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '10%' }]}>
                                <Text style={iaqInsight2PdfStyles.tableCellText}>
                                    {spaceMeasurementData.floor !== undefined
                                        ? spaceMeasurementData.floor.toString()
                                        : '-'}
                                </Text>
                            </View>

                            {/* Space name */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '30%' }]}>
                                <Text style={iaqInsight2PdfStyles.spaceNameText}>
                                    {spaceMeasurementData.name}
                                    {!averageValueStatus &&
                                        !lowestMeasurementStatus &&
                                        !highestMeasurementStatus &&
                                        ' *'}
                                </Text>
                            </View>

                            {/* Average */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {averageValueStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {averageValueStatus.value}
                                            {averageValueStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {averageValueStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && averageValueStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: averageValueStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${averageValueStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>

                            {/* Lowest */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {lowestMeasurementStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {lowestMeasurementStatus.value}
                                            {lowestMeasurementStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {lowestMeasurementStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && lowestMeasurementStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: lowestMeasurementStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${lowestMeasurementStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>

                            {/* Highest */}
                            <View style={[iaqInsight2PdfStyles.tableCell, { width: '20%' }]}>
                                {highestMeasurementStatus ? (
                                    <View>
                                        <Text style={iaqInsight2PdfStyles.tableCellText}>
                                            {highestMeasurementStatus.value}
                                            {highestMeasurementStatus.unit && (
                                                <Text style={iaqInsight2PdfStyles.tableCellUnit}>
                                                    {' '}
                                                    {highestMeasurementStatus.unit}
                                                </Text>
                                            )}
                                        </Text>
                                        {sensorShowsRatingLabels(sensorType) && highestMeasurementStatus.rating && (
                                            <Text
                                                style={[
                                                    iaqInsight2PdfStyles.ratingText,
                                                    { color: highestMeasurementStatus.color },
                                                ]}
                                            >
                                                {translationFunction(`Ratings.${highestMeasurementStatus.rating}`)}
                                            </Text>
                                        )}
                                    </View>
                                ) : (
                                    <Text style={iaqInsight2PdfStyles.tableCellText}>-</Text>
                                )}
                            </View>
                        </View>
                    </View>
                );
            })}

            {/* Footnote for spaces without data */}
            {spaceMeasurementColumns.some(
                row =>
                    row.average === undefined &&
                    row.lowestMeasurement === undefined &&
                    row.highestMeasurement === undefined
            ) && (
                <View style={{ marginTop: 8 }}>
                    <Text style={{ fontSize: 9, color: '#6B7280' }}>
                        * {translationFunction('IAQReport2.NoDataInSelectedTimePeriod')}
                    </Text>
                </View>
            )}
        </View>
    );
};

export default IaqInsight2DataTable;
